import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const VideoRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const lastSignalRef = useRef(0);
  const offerStartedRef = useRef(false);
  const signalBaseRef = useRef('');
  const leavingRef = useRef(false);
  const cleanupRef = useRef(() => {});
  const clientIdRef = useRef(`video-client-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [status, setStatus] = useState('Starting camera...');
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [facingMode, setFacingMode] = useState('user');

  useEffect(() => {
    let cancelled = false;
    let pollTimer;
    const clientId = clientIdRef.current;
    const signalBase = `${API_ENDPOINTS.BASE_URL}/api/parents/video-room/${roomId}`;
    signalBaseRef.current = signalBase;

    const cleanupMedia = () => {
      window.clearTimeout(pollTimer);
      window.clearInterval(peerRef.current?.participantCheck);
      peerRef.current?.close();
      peerRef.current = null;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (localVideoRef.current) localVideoRef.current.srcObject = null;
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      setConnected(false);
    };
    cleanupRef.current = cleanupMedia;

    const sendSignal = async (type, payload) => {
      await fetch(`${signalBase}/signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, type, payload })
      });
    };

    const createPeer = () => {
      const peer = new RTCPeerConnection({ iceServers: [] });
      peer.onicecandidate = (event) => {
        if (event.candidate) sendSignal('ice', event.candidate).catch(() => {});
      };
      peer.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
        setConnected(true);
        setStatus('Connected directly inside Creative Town.');
      };
      peer.onconnectionstatechange = () => {
        if (peer.connectionState === 'connected') {
          setConnected(true);
          setStatus('Connected directly inside Creative Town.');
        } else if (['failed', 'disconnected', 'closed'].includes(peer.connectionState)) {
          setConnected(false);
          setStatus('The other participant disconnected. Waiting for them to rejoin.');
        }
      };
      streamRef.current.getTracks().forEach((track) => peer.addTrack(track, streamRef.current));
      peerRef.current = peer;
      return peer;
    };

    const startOffer = async () => {
      if (offerStartedRef.current || !peerRef.current) return;
      offerStartedRef.current = true;
      const offer = await peerRef.current.createOffer();
      await peerRef.current.setLocalDescription(offer);
      await sendSignal('offer', offer);
      setStatus('Calling. Waiting for the other participant...');
    };

    const handleSignals = async (messages) => {
      for (const message of messages) {
        lastSignalRef.current = Math.max(lastSignalRef.current, message.createdAt);
        const peer = peerRef.current;
        if (!peer) continue;

        if (message.type === 'offer' && peer.signalingState === 'stable') {
          await peer.setRemoteDescription(new RTCSessionDescription(message.payload));
          const answer = await peer.createAnswer();
          await peer.setLocalDescription(answer);
          await sendSignal('answer', answer);
          setStatus('Joining call...');
        } else if (message.type === 'answer' && peer.signalingState === 'have-local-offer') {
          await peer.setRemoteDescription(new RTCSessionDescription(message.payload));
        } else if (message.type === 'ice' && message.payload) {
          try {
            await peer.addIceCandidate(new RTCIceCandidate(message.payload));
          } catch (error) {
            console.error('Unable to add video ICE candidate:', error);
          }
        } else if (message.type === 'hangup') {
          cancelled = true;
          leavingRef.current = true;
          cleanupMedia();
          setStatus('The call has ended.');
          window.setTimeout(() => navigate(-1), 700);
          return;
        }
      }
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        const response = await fetch(`${signalBase}/signal/${clientId}?since=${lastSignalRef.current}`);
        if (response.ok) {
          await handleSignals(await response.json());
        }
        const callResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/calls/room/${encodeURIComponent(roomId)}`);
        if (callResponse.ok) {
          const call = await callResponse.json();
          if (['ended', 'cancelled', 'declined'].includes(call.status)) {
            cancelled = true;
            leavingRef.current = true;
            cleanupMedia();
            setStatus('The call has ended.');
            window.setTimeout(() => navigate(-1), 700);
            return;
          }
        }
      } catch (error) {
        console.error('Video signaling poll failed:', error);
      }
      pollTimer = window.setTimeout(poll, 1000);
    };

    const start = async () => {
      try {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (localVideoRef.current) localVideoRef.current.srcObject = streamRef.current;
        createPeer();

        const joinResponse = await fetch(`${signalBase}/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clientId })
        });
        const join = await joinResponse.json();
        setStatus(join.participantCount > 1 ? 'Joining call...' : 'Waiting for the other participant...');
        if (join.role === 'caller' && join.participantCount > 1) {
          await startOffer();
        }
        poll();

        const participantCheck = window.setInterval(async () => {
          if (cancelled || connected || offerStartedRef.current) return;
          const response = await fetch(`${signalBase}/join`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ clientId })
          });
          const current = await response.json();
          if (current.role === 'caller' && current.participantCount > 1) {
            await startOffer();
          }
        }, 1500);
        peerRef.current.participantCheck = participantCheck;
      } catch (error) {
        setStatus('Camera and microphone permission are required for the video call.');
      }
    };

    start();
    return () => {
      cancelled = true;
      cleanupMedia();
      fetch(`${signalBase}/${clientId}`, { method: 'DELETE' }).catch(() => {});
    };
  }, [navigate, roomId]);

  const leaveRoom = async () => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setStatus('Leaving call...');
    const clientId = clientIdRef.current;
    try {
      await fetch(`${signalBaseRef.current}/signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, type: 'hangup', payload: { reason: 'participant-left' } })
      });
      await fetch(`${API_ENDPOINTS.BASE_URL}/api/calls/room/${encodeURIComponent(roomId)}/end`, { method: 'PUT' });
      await fetch(`${signalBaseRef.current}/${clientId}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Unable to notify the other participant:', error);
    } finally {
      cleanupRef.current();
      window.open('', '_self');
      window.close();
      window.setTimeout(() => {
        if (!window.closed) {
          const fallback = localStorage.getItem('parentUser')
            ? '/parent/dashboard'
            : localStorage.getItem('teacherUser')
              ? '/teacher-dashboard'
              : '/student/dashboard';
          navigate(fallback, { replace: true });
        }
      }, 250);
    }
  };

  const toggleMute = () => {
    const nextMuted = !muted;
    streamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    setMuted(nextMuted);
  };

  const toggleCamera = () => {
    const nextCameraOff = !cameraOff;
    streamRef.current?.getVideoTracks().forEach((track) => {
      track.enabled = !nextCameraOff;
    });
    setCameraOff(nextCameraOff);
  };

  const switchCamera = async () => {
    const nextFacingMode = facingMode === 'user' ? 'environment' : 'user';
    try {
      const replacementStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: nextFacingMode } },
        audio: true
      });
      const replacementVideoTrack = replacementStream.getVideoTracks()[0];
      const sender = peerRef.current?.getSenders().find((item) => item.track?.kind === 'video');
      if (sender && replacementVideoTrack) {
        await sender.replaceTrack(replacementVideoTrack);
      }
      streamRef.current?.getVideoTracks().forEach((track) => track.stop());
      const audioTracks = streamRef.current?.getAudioTracks() || replacementStream.getAudioTracks();
      streamRef.current = new MediaStream([...audioTracks, replacementVideoTrack]);
      if (localVideoRef.current) localVideoRef.current.srcObject = streamRef.current;
      replacementStream.getAudioTracks().forEach((track) => {
        if (!streamRef.current.getAudioTracks().includes(track)) track.stop();
      });
      setFacingMode(nextFacingMode);
      setCameraOff(false);
    } catch (error) {
      setStatus('Camera switching is not supported on this device.');
    }
  };

  const toggleSpeaker = () => {
    const nextSpeakerMuted = !speakerMuted;
    if (remoteVideoRef.current) remoteVideoRef.current.muted = nextSpeakerMuted;
    setSpeakerMuted(nextSpeakerMuted);
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      setStatus('Fullscreen is not supported by this browser.');
    }
  };

  const copyRoomLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setStatus('Room link copied. Send it to the parent or student.');
    } catch (error) {
      setStatus('Unable to copy automatically. Copy the address from the browser.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-sky-300 font-bold uppercase text-sm">Creative Town Video Room</p>
            <h1 className="text-2xl md:text-3xl font-black mt-1 break-all">{roomId}</h1>
            <p className={connected ? 'text-emerald-300 mt-2' : 'text-amber-300 mt-2'}>{status}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={leaveRoom} className="bg-slate-700 text-white px-5 py-3 rounded-lg font-bold hover:bg-slate-600">Leave Room</button>
            <button onClick={leaveRoom} className="bg-rose-600 text-white px-5 py-3 rounded-lg font-bold hover:bg-rose-700">End Call</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div>
            <p className="text-sm font-bold text-slate-300 mb-2">You</p>
            <div className="bg-black rounded-xl overflow-hidden border border-slate-700 aspect-video">
              <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>
          </div>
          <div>
            <p className="text-sm font-bold text-slate-300 mb-2">Parent or Student</p>
            <div className="bg-black rounded-xl overflow-hidden border border-slate-700 aspect-video relative">
              <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
              {!connected && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                  <div className="text-center">
                    <div className="mx-auto h-20 w-20 rounded-full border-4 border-sky-400 animate-pulse flex items-center justify-center font-black">Call</div>
                    <p className="text-slate-300 mt-4">Waiting for the second participant</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          <button onClick={toggleMute} className={`px-5 py-3 rounded-lg font-bold ${muted ? 'bg-amber-500 text-slate-950' : 'bg-slate-700 text-white'}`}>
            {muted ? 'Unmute' : 'Mute'}
          </button>
          <button onClick={toggleCamera} className={`px-5 py-3 rounded-lg font-bold ${cameraOff ? 'bg-amber-500 text-slate-950' : 'bg-slate-700 text-white'}`}>
            {cameraOff ? 'Turn Camera On' : 'Turn Camera Off'}
          </button>
          <button onClick={toggleSpeaker} className={`px-5 py-3 rounded-lg font-bold ${speakerMuted ? 'bg-amber-500 text-slate-950' : 'bg-slate-700 text-white'}`}>
            {speakerMuted ? 'Unmute Speaker' : 'Mute Speaker'}
          </button>
          <button onClick={switchCamera} className="px-5 py-3 rounded-lg font-bold bg-sky-700 text-white hover:bg-sky-800">
            Switch Camera
          </button>
          <button onClick={toggleFullscreen} className="px-5 py-3 rounded-lg font-bold bg-indigo-700 text-white hover:bg-indigo-800">
            Fullscreen
          </button>
          <button onClick={copyRoomLink} className="px-5 py-3 rounded-lg font-bold bg-emerald-700 text-white hover:bg-emerald-800">
            Copy Room Link
          </button>
          <button onClick={leaveRoom} className="px-5 py-3 rounded-lg font-bold bg-rose-600 text-white hover:bg-rose-700">
            End Call
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoRoom;
